import tinycolor from 'tinycolor2';

/**
 * The palette shared by the CBOS pages, derived from one base colour.
 *
 * The family is the point: the dashboard's process steps are shades of one base, so the rail reads
 * as a progression rather than as unrelated colours, and the heatmap sits on the same ground so the
 * two look like one product rather than two.
 *
 * The base used to be a fixed navy, deliberately outside the customer theme. That was wrong in
 * practice, not in principle — the phases then showed a blue that belonged to no customer, which
 * next to a brand-coloured interface reads as an oversight rather than as a decision. So the family
 * is built from the customer's primary colour instead; the progression is unchanged, only its
 * starting point now belongs to the customer.
 */

/**
 * The fallback, for when no customer colour has been set.
 *
 * Not a placeholder: before the theme has been fetched, in tests, and for a tenant without a theme,
 * this is what the pages are painted in.
 */
export const baseColor = '#001D34';

/**
 * The customer's primary colour, read from the stylesheet.
 *
 * Only the default for a caller that has none to give. It is not enough on its own: the theme
 * provider fetches in an effect and renders its children first, so during that first render the
 * variable is not set yet — and nothing hangs off a CSS variable that would redraw once it is.
 * `useBrandBase` is the reliable source; this keeps the palette usable outside React.
 */
export const brandBase = (): string => {
    if (typeof document === 'undefined') return baseColor;

    const value = getComputedStyle(document.body).getPropertyValue('--customer-primary').trim();
    const parsed = tinycolor(value);

    return value && parsed.isValid() ? parsed.toHexString() : baseColor;
};

/** Deepest first: step 1 is the base itself, later steps lighten along the way. */
const STEP_LIGHTEN = [0, 10, 20, 30];

export const stepColor = (index: number, base: string = brandBase()): string =>
    tinycolor(base)
        .lighten(STEP_LIGHTEN[index] ?? STEP_LIGHTEN[STEP_LIGHTEN.length - 1])
        .toHexString();

/** Base at low opacity — icon plates, active drop zones. */
export const tintOf = (color: string, alpha = 0.14): string => tinycolor(color).setAlpha(alpha).toRgbString();

/** The page ground: the base washed almost all the way out to white. */
export const pageBackground = (base: string = brandBase()): string =>
    tinycolor.mix(base, '#ffffff', 95).toHexString();

/** Secondary text — the base at reduced weight, matching `$inkMuted` in the stylesheet. */
export const mutedInk = (base: string = brandBase()): string => tinycolor(base).setAlpha(0.62).toRgbString();
